import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useDocumentById, useNotify } from "hooks";
import { Drawer, Loading, BillModal } from "components";
import { NavigationContext } from "components/file-viewer-footer";
import { useDocumentUploadNavigation } from "recoils";

import { ADD_DOCUMENT_BILL_PAGE } from "../consts";
import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "../../document-summary-v2/utility-bill/utility-bill-allDataEntries-view/consts";

export const AddDocumentBillPage = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { documentId } = useParams<{ [ADD_DOCUMENT_BILL_PAGE.KEY]: string | undefined }>();
  const { data: document, isLoading, error } = useDocumentById(documentId);

  const [isOpen, setIsOpen] = useState<boolean>(true);

  const onClose = useCallback(async () => {
    navigate(-1)
    setIsOpen(false);
    window.localStorage.removeItem("utilityBillFormData");
  }, [navigate]);

  useEffect(() => {
    if (!isLoading && (error !== null || document === null || document === undefined)) {
      navigate(-1);
      notify.error("Manual data entries are disabled for system-generated bills.");
    }
  }, [document, navigate, notify, error, isLoading]);

  const docUploadNav = useDocumentUploadNavigation();

  const prevDocRef = useRef(document);
  if (document) prevDocRef.current = document;
  const displayDocument = document ?? prevDocRef.current;

  const handleNavigate = useCallback((id: string) => {
    const entry = docUploadNav?.entries.find((e) => e.navId === id);
    if (entry?.billIds.length) {
      navigate(UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(id));
    } else {
      navigate(ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(id));
    }
  }, [navigate, docUploadNav]);

  const navigationContext = useMemo<NavigationContext | undefined>(() => {
    if (!docUploadNav || !documentId) return undefined;

    const currentIndex = Math.max(
      0,
      docUploadNav.entries.findIndex((e) => e.navId === String(documentId)),
    );

    return {
      mode: "document",
      ids: docUploadNav.entries.map((e) => e.navId),
      currentIndex,
      totalElements: docUploadNav.totalCount,
      fetchNextPage: () => Promise.resolve(),
    };
  }, [docUploadNav, documentId]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      zIndex={1300}
      customPaperProps={{ width: "100%", height: "auto", overflow: "hidden" }}
      expandedWidth='100%'
    >
      {displayDocument ? (
        <BillModal
          document={displayDocument}
          isLoading={isLoading}
          onClose={onClose}
          navigationContext={navigationContext}
          activeTab="document"
          onNavigate={handleNavigate}
        />
      ) : (
        <Loading />
      )}
    </Drawer>
  );
};
